import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { artworkApi as A, ART_STATUS_STYLE } from "@/lib/artwork";
import { customersApi } from "@/lib/customers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Search, Loader2 } from "lucide-react";

export default function ArtworkList() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ artwork_statuses: [], repeat_types: [] });
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ customer_id: "", name: "", design_ref: "", repeat_type: "", colours: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try { const [s, list] = await Promise.all([A.stats(), A.list({ search: search || undefined, status })]); setStats(s); setRows(list); }
    finally { setLoading(false); }
  };
  useEffect(() => { A.meta().then(setMeta); customersApi.list({ status: "Active" }).then(setCustomers).catch(() => {}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const save = async () => {
    if (!f.customer_id) { toast.error("Select a customer — artwork must belong to a customer"); return; }
    if (!f.name) { toast.error("Artwork name is required"); return; }
    setSaving(true);
    try { const res = await A.create(f); toast.success("Artwork created"); setOpen(false); navigate(`/artwork/${res.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="artwork-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Artwork Management</h2>
          <p className="mt-1 text-sm text-slate-500">Single source of truth for all customer artwork.</p>
        </div>
        {can("artwork", "create") && (
          <Button onClick={() => setOpen(true)} data-testid="new-artwork-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />New Artwork</Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-4" data-testid="art-stat-total"><p className="text-xs uppercase tracking-wider text-slate-500">Total</p><p className="mt-1 font-heading text-2xl font-bold text-slate-900">{stats.total ?? 0}</p></Card>
        <Card className="p-4" data-testid="art-stat-pending"><p className="text-xs uppercase tracking-wider text-slate-500">Pending approval</p><p className="mt-1 font-heading text-2xl font-bold text-amber-600">{stats.pending ?? 0}</p></Card>
        <Card className="p-4" data-testid="art-stat-approved"><p className="text-xs uppercase tracking-wider text-slate-500">Approved</p><p className="mt-1 font-heading text-2xl font-bold text-teal-600">{stats.approved ?? 0}</p></Card>
        <Card className="p-4" data-testid="art-stat-locked"><p className="text-xs uppercase tracking-wider text-slate-500">Locked (print-ready)</p><p className="mt-1 font-heading text-2xl font-bold text-emerald-600">{stats.locked ?? 0}</p></Card>
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code / name / customer / design ref" className="pl-9" data-testid="art-search-input" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="art-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.artwork_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Code</TableHead><TableHead className="text-xs uppercase text-slate-500">Name</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead><TableHead className="text-xs uppercase text-slate-500">Version</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/artwork/${r.id}`)} data-testid={`artwork-row-${r.artwork_code}`}>
                    <TableCell className="font-medium text-slate-800">{r.artwork_code}</TableCell>
                    <TableCell className="text-slate-700">{r.name}<span className="block text-xs text-slate-400">{r.design_ref}</span></TableCell>
                    <TableCell className="text-slate-600">{r.customer_name}</TableCell>
                    <TableCell className="text-slate-600">V{r.current_version}{r.locked_version ? ` · locked V${r.locked_version}` : ""}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${ART_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-12 text-center text-slate-400" data-testid="art-empty">No artwork yet. Create one and link it to a customer.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader><SheetTitle>New Artwork</SheetTitle><SheetDescription>Register customer artwork. Version 1 starts as Draft.</SheetDescription></SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-1.5"><Label>Customer *</Label>
              <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                <SelectTrigger data-testid="art-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Artwork name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="art-name-input" /></div>
            <div className="space-y-1.5"><Label>Customer design ref</Label><Input value={f.design_ref} onChange={(e) => setF({ ...f, design_ref: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Repeat type</Label>
                <Select value={f.repeat_type} onValueChange={(v) => setF({ ...f, repeat_type: v })}>
                  <SelectTrigger data-testid="art-repeat-select"><SelectValue placeholder="Repeat" /></SelectTrigger>
                  <SelectContent>{meta.repeat_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>No. of colours</Label><Input value={f.colours} onChange={(e) => setF({ ...f, colours: e.target.value })} /></div>
            </div>
          </div>
          <SheetFooter className="mt-8">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-artwork-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
